import React, { useEffect, useState, useCallback } from 'react';
import { fetchGroceryReport, fetchMealsReport, fetchPetsReport, fetchChoresReport } from '../api';

const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function StatCard({ label, value, sub }) {
  return (
    <div style={rs.statCard}>
      <span style={rs.statValue}>{value}</span>
      <span style={rs.statLabel}>{label}</span>
      {sub && <span style={rs.statSub}>{sub}</span>}
    </div>
  );
}

function BarRow({ label, count, max, color = 'var(--accent)' }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div style={rs.barRow}>
      <span style={rs.barLabel}>{label}</span>
      <div style={rs.barTrack}>
        <div style={{ ...rs.barFill, width: `${pct}%`, background: color }} />
      </div>
      <span style={rs.barCount}>{count}</span>
    </div>
  );
}

function Section({ title, dot, children }) {
  return (
    <div style={rs.section}>
      <div style={rs.sectionHeader}>
        <span style={{ ...rs.sectionDot, background: dot }} />
        <span style={rs.sectionTitle}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ── Grocery Report ──────────────────────────────────────────────────────────

function GroceryReport() {
  const [data, setData] = useState(null);
  useEffect(() => { fetchGroceryReport().then(setData).catch(() => {}); }, []);
  if (!data) return <p style={rs.loading}>Loading…</p>;
  const max = data.items[0]?.count || 1;
  return (
    <div style={rs.reportBody}>
      <div style={rs.statRow}>
        <StatCard label="Items purchased" value={data.total_purchases} sub="last 365 days" />
        <StatCard label="Unique items" value={data.items.length} />
      </div>
      {data.items.length === 0 ? (
        <p style={rs.empty}>No grocery history yet — items are recorded when checked off.</p>
      ) : (
        <>
          <p style={rs.subheading}>Most purchased</p>
          {data.items.map(item => (
            <BarRow key={item.name} label={item.name} count={item.count} max={max} color="var(--accent)" />
          ))}
        </>
      )}
    </div>
  );
}

// ── Meals Report ──────────────────────────────────────────────────────────

function MealsReport() {
  const [data, setData] = useState(null);
  useEffect(() => { fetchMealsReport().then(setData).catch(() => {}); }, []);
  if (!data) return <p style={rs.loading}>Loading…</p>;
  const maxMeal = data.top_meals[0]?.count || 1;
  const maxCook = data.cook_stats[0]?.count || 1;
  const maxDow  = Math.max(...data.dow_stats.map(d => d.count), 1);
  return (
    <div style={rs.reportBody}>
      <div style={rs.statRow}>
        <StatCard label="Meals planned" value={data.total_meals} sub="last 365 days" />
        <StatCard label="Unique meals" value={data.top_meals.length} />
        <StatCard label="Cooks" value={data.cook_stats.length} />
      </div>

      {data.top_meals.length === 0 ? (
        <p style={rs.empty}>No meal history yet.</p>
      ) : (
        <>
          <p style={rs.subheading}>Most cooked meals</p>
          {data.top_meals.map(m => (
            <BarRow key={m.meal_name} label={m.meal_name} count={m.count} max={maxMeal} color="var(--accent3)" />
          ))}

          {data.cook_stats.length > 0 && (
            <>
              <p style={rs.subheading}>Most active chef</p>
              {data.cook_stats.map(c => (
                <BarRow key={c.name} label={c.name} count={c.count} max={maxCook} color={c.color} />
              ))}
            </>
          )}

        </>
      )}
    </div>
  );
}

// ── Pets Report ──────────────────────────────────────────────────────────

function PetsReport() {
  const [data, setData] = useState(null);
  useEffect(() => { fetchPetsReport().then(setData).catch(() => {}); }, []);
  if (!data) return <p style={rs.loading}>Loading…</p>;

  return (
    <div style={rs.reportBody}>
      <p style={rs.subheading}>Feeding completion</p>
      {data.feeding_stats.length === 0 ? (
        <p style={rs.empty}>No feeding data yet.</p>
      ) : (
        data.feeding_stats.map(pet => {
          const pct = pet.days_logged > 0
            ? Math.round((pet.days_complete / pet.days_logged) * 100)
            : 0;
          return (
            <div key={pet.name} style={rs.petStatRow}>
              <span style={rs.barLabel}>{pet.name}</span>
              <div style={rs.barTrack}>
                <div style={{
                  ...rs.barFill,
                  width: `${pct}%`,
                  background: pct >= 80 ? 'var(--accent3)' : pct >= 50 ? 'var(--accent)' : 'var(--danger)',
                }} />
              </div>
              <span style={rs.barCount}>{pct}%</span>
              <span style={rs.petStatSub}>{pet.days_complete}/{pet.days_logged} days</span>
            </div>
          );
        })
      )}

      {data.walk_stats.length > 0 && (
        <>
          <p style={{...rs.subheading, marginTop: 20}}>Walk completion</p>
          {data.walk_stats.map(w => {
            const pct = w.days_total > 0
              ? Math.round((w.days_complete / w.days_total) * 100)
              : 0;
            return (
              <div key={w.walk_type} style={rs.petStatRow}>
                <span style={rs.barLabel}>
                  {w.walk_type.charAt(0).toUpperCase() + w.walk_type.slice(1)} walk
                </span>
                <div style={rs.barTrack}>
                  <div style={{
                    ...rs.barFill,
                    width: `${pct}%`,
                    background: pct >= 80 ? 'var(--accent3)' : 'var(--accent)',
                  }} />
                </div>
                <span style={rs.barCount}>{pct}%</span>
                <span style={rs.petStatSub}>{w.days_complete}/{w.days_total} days</span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}


// ── Chores Report ─────────────────────────────────────────────────────────

function ChoresReport() {
  const [data, setData] = useState(null);
  useEffect(() => { fetchChoresReport().then(setData).catch(() => {}); }, []);
  if (!data) return <p style={rs.loading}>Loading…</p>;
  const maxPerson = data.by_person[0]?.completions || 1;
  const maxChore  = data.top_chores[0]?.completions || 1;
  return (
    <div style={rs.reportBody}>
      <div style={rs.statRow}>
        <StatCard label="Completions" value={data.total_completions} sub="last 365 days" />
        <StatCard label="Missed" value={data.total_misses} sub="last 365 days" />
        <StatCard label="Chores tracked" value={data.compliance.length} />
      </div>

      {data.by_person.length === 0 ? (
        <p style={rs.empty}>No completions yet — chore compliance will appear here once chores are being marked done.</p>
      ) : (
        <>
          <p style={rs.subheading}>Most compliant person</p>
          {data.by_person.map(p => (
            <BarRow key={p.name} label={p.name} count={p.completions} max={maxPerson} color={p.color} />
          ))}
        </>
      )}

      {data.top_chores.length > 0 && (
        <>
          <p style={rs.subheading}>Most completed chores</p>
          {data.top_chores.map(c => (
            <BarRow key={c.name + c.room} label={`${c.name} (${c.room})`} count={c.completions} max={maxChore} color="var(--accent2)" />
          ))}
        </>
      )}

      {data.compliance.filter(c => c.total > 0).length > 0 && (
        <>
          <p style={rs.subheading}>Compliance rate by chore</p>
          {data.compliance.filter(c => c.total > 0).map(c => (
            <div key={c.name + c.room} style={rs.complianceRow}>
              <span style={rs.barLabel}>{c.name} <span style={{opacity:0.5, fontSize:'0.75em'}}>({c.room})</span></span>
              <div style={rs.barTrack}>
                <div style={{
                  ...rs.barFill,
                  width: `${c.rate}%`,
                  background: c.rate >= 80 ? 'var(--accent3)' : c.rate >= 50 ? 'var(--accent)' : 'var(--danger)',
                }} />
              </div>
              <span style={rs.barCount}>{c.rate}%</span>
              <span style={{...rs.barCount, width: 60, color: 'var(--text-dimmer)', fontSize: '0.62rem'}}>
                {c.completions}✓ {c.misses}✗
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── Main Reports Panel ────────────────────────────────────────────────────

const REPORT_TABS = [
  { id: 'meals',   label: 'Meals',   dot: 'var(--accent3)' },
  { id: 'grocery', label: 'Grocery', dot: 'var(--accent)'  },
  { id: 'pets',    label: 'Pets',    dot: '#e87ab8'         },
  { id: 'chores',  label: 'Chores',  dot: 'var(--accent2)' },
];

export default function Reports() {
  const [tab, setTab] = useState('meals');

  return (
    <div style={rs.root}>
      <div style={rs.header}>
        <span style={rs.title}>Reports</span>
        <span style={rs.subtitle}>Last 365 days</span>
      </div>

      <div style={rs.tabs}>
        {REPORT_TABS.map(t => (
          <button
            key={t.id}
            style={{ ...rs.tab, ...(tab === t.id ? rs.tabActive : {}) }}
            onClick={() => setTab(t.id)}
          >
            {tab === t.id && <span style={{ ...rs.tabDot, background: t.dot }} />}
            {t.label}
          </button>
        ))}
      </div>

      <div style={rs.content}>
        {tab === 'meals'   && <Section title="Meal Statistics"   dot="var(--accent3)"><MealsReport /></Section>}
        {tab === 'grocery' && <Section title="Grocery History"   dot="var(--accent)"><GroceryReport /></Section>}
        {tab === 'pets'    && <Section title="Pet Care"          dot="#e87ab8"><PetsReport /></Section>}
        {tab === 'chores'  && <Section title="Chore Compliance"  dot="var(--accent2)"><ChoresReport /></Section>}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────

const rs = {
  root: {
    display: 'flex', flexDirection: 'column',
    height: '100%', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'baseline', gap: 12,
    padding: '4px 0 12px', flexShrink: 0,
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.4rem', color: 'var(--accent)', fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
    color: 'var(--text-dimmer)', letterSpacing: '0.1em',
  },
  tabs: {
    display: 'flex', gap: 6, flexShrink: 0, marginBottom: 16,
  },
  tab: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--text-dimmer)', background: 'transparent',
    border: '1px solid var(--border)', borderRadius: 20,
    padding: '4px 14px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 5,
  },
  tabActive: {
    color: 'var(--text)', border: '1px solid var(--surface2)',
    background: 'var(--surface2)',
  },
  tabDot: {
    width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
  },
  content: {
    flex: 1, overflowY: 'auto',
  },
  section: {
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
  },
  sectionDot: {
    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
    boxShadow: '0 0 6px currentColor',
  },
  sectionTitle: {
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
    letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-dimmer)',
  },
  reportBody: {
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  statRow: {
    display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap',
  },
  statCard: {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', padding: '10px 16px',
    display: 'flex', flexDirection: 'column', gap: 2, minWidth: 100,
  },
  statValue: {
    fontFamily: 'var(--font-display)', fontSize: '1.6rem',
    color: 'var(--accent)', lineHeight: 1, fontStyle: 'italic',
  },
  statLabel: {
    fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
    color: 'var(--text-dimmer)', letterSpacing: '0.1em', textTransform: 'uppercase',
  },
  statSub: {
    fontFamily: 'var(--font-mono)', fontSize: '0.58rem', color: 'var(--text-dimmer)',
  },
  subheading: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: 'var(--text-dimmer)', marginTop: 12, marginBottom: 4,
  },
  barRow: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  barLabel: {
    fontFamily: 'var(--font-body)', fontSize: '0.85rem',
    color: 'var(--text)', width: 140, flexShrink: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  barTrack: {
    flex: 1, height: 6, background: 'var(--surface2)',
    borderRadius: 3, overflow: 'hidden',
  },
  barFill: {
    height: '100%', borderRadius: 3, transition: 'width 0.4s ease',
  },
  barCount: {
    fontFamily: 'var(--font-mono)', fontSize: '0.72rem',
    color: 'var(--text-dim)', width: 28, textAlign: 'right', flexShrink: 0,
  },
  petStatRow: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  petStatSub: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    color: 'var(--text-dimmer)', flexShrink: 0,
  },
  complianceRow: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  loading: {
    fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
    color: 'var(--text-dimmer)', fontStyle: 'italic',
  },
  empty: {
    fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
    color: 'var(--text-dimmer)', fontStyle: 'italic', lineHeight: 1.6,
  },
};
